import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ScoreHistoryModal from './ScoreHistoryModal';

interface CompactStatsPanelProps {
    teamAScore: number;
    teamBScore: number;
    teamAAlignments: number;
    teamBAlignments: number;
    currentRound: number;
    maxRounds: number;
    roundScores: Array<{
        teamA: number;
        teamB: number;
    }>;
}

const CompactStatsPanel: React.FC<CompactStatsPanelProps> = ({
    teamAScore,
    teamBScore,
    teamAAlignments,
    teamBAlignments,
    currentRound,
    maxRounds,
    roundScores
}) => {
    const [showHistory, setShowHistory] = useState(false);

    const scoreDiff = Math.abs(teamAScore - teamBScore);
    const leader = teamAScore > teamBScore ? 'A' : teamAScore < teamBScore ? 'B' : null;
    const teamAPercentage = teamAScore + teamBScore > 0
        ? (teamAScore / (teamAScore + teamBScore)) * 100
        : 50;

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-md)',
            width: '100%'
        }}>
            {/* Header with History Button */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    color: 'var(--text-primary)',
                    margin: 0
                }}>
                    📊 Scores
                </h3>
                <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        Tour {currentRound}/{maxRounds}
                    </span>
                    <button
                        onClick={() => setShowHistory(true)}
                        className="btn btn-secondary"
                        style={{
                            padding: '6px 12px',
                            minWidth: 'auto',
                            fontSize: '0.875rem'
                        }}
                    >
                        📈
                    </button>
                </div>
            </div>

            {/* Compact Scores */}
            <div className="glass-card" style={{
                padding: 'var(--space-md)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-sm)'
            }}>
                {/* Team A */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span style={{
                        fontSize: '0.875rem',
                        color: 'var(--team-a-light)',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        🔵 Équipe A
                        {leader === 'A' && <span style={{ fontSize: '1rem' }}>👑</span>}
                    </span>
                    <span style={{
                        fontSize: '1.5rem',
                        fontWeight: '800',
                        background: 'linear-gradient(135deg, var(--team-a), #60A5FA)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>
                        {Math.round(teamAScore)}
                    </span>
                </div>

                {/* Progress Bar */}
                <div className="progress-bar" style={{ height: '6px' }}>
                    <motion.div
                        className="progress-fill"
                        animate={{ width: `${teamAPercentage}%` }}
                        transition={{ duration: 0.5 }}
                    />
                </div>

                {/* Team B */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span style={{
                        fontSize: '0.875rem',
                        color: 'var(--team-b-light)',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        🔴 Équipe B
                        {leader === 'B' && <span style={{ fontSize: '1rem' }}>👑</span>}
                    </span>
                    <span style={{
                        fontSize: '1.5rem',
                        fontWeight: '800',
                        background: 'linear-gradient(135deg, var(--team-b), #F87171)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>
                        {Math.round(teamBScore)}
                    </span>
                </div>

                {/* Gap */}
                {leader && scoreDiff > 0 && (
                    <div style={{
                        marginTop: 'var(--space-xs)',
                        padding: 'var(--space-sm)',
                        background: 'var(--bg-card)',
                        borderRadius: 'var(--radius-sm)',
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        color: leader === 'A' ? 'var(--team-a)' : 'var(--team-b)',
                        fontWeight: '600'
                    }}>
                        Écart: {scoreDiff} pts
                    </div>
                )}
            </div>

            {/* Alignments - Compact */}
            <div className="glass-card" style={{ padding: 'var(--space-md)' }}>
                <h4 style={{
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    marginBottom: 'var(--space-sm)',
                    color: 'var(--text-primary)'
                }}>
                    🎯 Alignements
                </h4>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 'var(--space-sm)',
                    fontSize: '0.75rem'
                }}>
                    <div>
                        <span style={{ color: 'var(--team-a-light)' }}>🔵 A: </span>
                        <strong style={{ color: 'var(--success)' }}>
                            {teamAAlignments}
                        </strong>
                        {teamAAlignments > 0 && (
                            <span style={{ color: 'var(--text-muted)', marginLeft: '4px' }}>
                                (×{(1 + teamAAlignments * 0.5).toFixed(1)})
                            </span>
                        )}
                    </div>
                    <div>
                        <span style={{ color: 'var(--team-b-light)' }}>🔴 B: </span>
                        <strong style={{ color: 'var(--success)' }}>
                            {teamBAlignments}
                        </strong>
                        {teamBAlignments > 0 && (
                            <span style={{ color: 'var(--text-muted)', marginLeft: '4px' }}>
                                (×{(1 + teamBAlignments * 0.5).toFixed(1)})
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Score History Modal */}
            <ScoreHistoryModal
                isOpen={showHistory}
                onClose={() => setShowHistory(false)}
                rounds={roundScores}
                currentRound={currentRound}
            />
        </div>
    );
};

export default CompactStatsPanel;
